import { useMemo, useState } from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { RegionSummaryCard } from "src/components/region-summary-card";
import { CardChart } from "src/components/mini-chart";
import { getDataRequest } from "src/utils/data";
import { useDataLake } from "src/hooks/use-datalake";
import { heatmap } from "src/utils/design";
import { paths } from "src/paths";
import { DataRanges } from "src/utils/configs";

/** levelLabel("시도"|"군구"|"동")을 DataRanges 코드(S|G|D)로 매핑 */
const levelToRange = (level) => {
  if (level === "시도") return DataRanges.SIDO;
  if (level === "군구") return DataRanges.GUNGU;
  if (level === "동") return DataRanges.DONG;
  return null;
};

/** 지역정보 새창 열기 — id와 dataRange를 쿼리로 전달 */
const openRegionNewWindow = (region) => {
  const range = levelToRange(region.levelLabel);
  if (!region?.id || !range) return;
  const url = `${paths.dashboard.regions.newwindow}?id=${region.id}&range=${range}&menu=false`;
  window.open(url, "_blank", `width=1200,height=${window.innerHeight}`);
};

/** 최근 N개 월별 변동율 추출 (월간 데이터 가정) */
const getRecentChanges = (response, count = 6) => {
  if (!response?.data?.length || !response?.dates?.length) return [];
  const data = response.data[0]?.data;
  const dates = response.dates;
  if (!data || data.length < 2) return [];

  const results = [];
  for (let i = data.length - 1; i > 0 && results.length < count; i--) {
    if (data[i] == null || data[i - 1] == null || data[i - 1] === 0) continue;
    const rate = ((data[i] - data[i - 1]) / data[i - 1]) * 100;
    const d = new Date(dates[i]);
    results.push({
      date: `${d.getMonth() + 1}월`,
      rate: Math.round(rate * 100) / 100,
    });
  }
  return results.reverse();
};

/**
 * 일별 시계열을 월별로 그룹화 후 마지막 월 값으로 변동율 계산.
 * 동 매물시세(MP daily)처럼 daily 응답에서 월별 변동을 표시할 때 사용.
 */
const getMonthlyChangesFromDaily = (response, count = 6) => {
  if (!response?.data?.length || !response?.dates?.length) return [];
  const data = response.data[0]?.data;
  const dates = response.dates;
  if (!data || data.length < 2) return [];

  // 월(YYYY-MM)별로 그 달의 마지막 valid 데이터 추출 (날짜 순회 가정 — 오름차순)
  const monthMap = new Map();
  for (let i = 0; i < data.length; i++) {
    const v = data[i];
    if (v == null) continue;
    const d = new Date(dates[i]);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    monthMap.set(key, { date: dates[i], value: v, m: d.getMonth() + 1 });
  }
  const monthly = Array.from(monthMap.values()).sort((a, b) => a.date - b.date);
  if (monthly.length < 2) return [];

  const results = [];
  for (let i = monthly.length - 1; i > 0 && results.length < count; i--) {
    const cur = monthly[i];
    const prev = monthly[i - 1];
    if (prev.value === 0) continue;
    const rate = ((cur.value - prev.value) / prev.value) * 100;
    results.push({ date: `${cur.m}월`, rate: Math.round(rate * 100) / 100 });
  }
  return results.reverse();
};

/** 변동율 미니 테이블 (최근 N개). 셀이 컨테이너 폭에 따라 균등 분배. */
const ChangeRateTable = ({ changes, label }) => {
  if (!changes || changes.length === 0) return null;
  return (
    <Stack direction="row" spacing={0} sx={{ mt: 0.3, width: "100%" }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 9, minWidth: 30, pt: 0.2, flexShrink: 0 }}
      >
        {label}
      </Typography>
      {changes.map((c, i) => (
        <Box key={i} sx={{ textAlign: "center", flex: 1, minWidth: 0, px: 0.2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 8, display: "block", lineHeight: 1.2 }}>
            {c.date}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: 9,
              fontWeight: 700,
              color: c.rate > 0 ? heatmap.increase : c.rate < 0 ? heatmap.decrease : "text.secondary",
              display: "block",
              lineHeight: 1.2,
            }}
          >
            {c.rate > 0 ? "+" : ""}{c.rate.toFixed(2)}%
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

/**
 * 관심 지역 카드 + 차트 (매매/전세 지수 or 시세).
 * 시도/군구: KB 매매/전세 지수 (PI, monthly)
 * 동: 네이버 매매/전세 매물시세 (MP, daily)
 *
 * @param region 지역 데이터
 * @param onClick 카드 클릭 핸들러
 * @param showChart true(기본): 차트 항상 표시 / false: "차트보기" 토글 버튼
 * @param fullWidth true: 부모 폭 100% (다이얼로그/세로 리스트용)
 */
export const RegionChartCard = ({ region, onClick, showChart = true, fullWidth = false, showStar = true, hideTitle = false }) => {
  const level = region.levelLabel;
  const id = region.id;

  // showChart=false일 때 사용자가 "차트보기"로 펼쳤는지 여부
  const [chartExpanded, setChartExpanded] = useState(false);
  const renderChart = showChart || chartExpanded;

  const requests = useMemo(() => {
    if (!id || !renderChart) return [];
    if (level === "시도") {
      return [
        getDataRequest("D", "PI", "M", "S", "KBS", { sidoId: id }),
        getDataRequest("L", "PI", "M", "S", "KBS", { sidoId: id }),
      ];
    } else if (level === "군구") {
      return [
        getDataRequest("D", "PI", "M", "G", "KBS", { gunguId: id }),
        getDataRequest("L", "PI", "M", "G", "KBS", { gunguId: id }),
      ];
    } else if (level === "동") {
      return [
        getDataRequest("D", "MP", "D", "D", "ONN", { dongId: id }),
        getDataRequest("L", "MP", "D", "D", "ONN", { dongId: id }),
      ];
    }
    return [];
  }, [id, level, renderChart]);

  const { initialized, responses } = useDataLake(requests);

  const seriesData = useMemo(() => {
    if (!initialized || requests.length < 2) return [];
    const deal = responses.getResponse(requests[0]);
    const lease = responses.getResponse(requests[1]);
    const result = [];
    if (deal?.data?.length > 0) result.push({ response: deal, name: "매매", color: "#1565C0" });
    if (lease?.data?.length > 0) result.push({ response: lease, name: "전세", color: "#E53935" });
    return result;
  }, [initialized, responses, requests]);

  const isDong = level === "동";

  // 변동율 계산 — 시도/군구는 월간 데이터(getRecentChanges), 동은 일간 → 월별 그룹화(getMonthlyChangesFromDaily)
  const dealChanges = useMemo(() => {
    if (!initialized || requests.length < 1) return [];
    const fn = isDong ? getMonthlyChangesFromDaily : getRecentChanges;
    return fn(responses.getResponse(requests[0]));
  }, [initialized, responses, requests, isDong]);

  const leaseChanges = useMemo(() => {
    if (!initialized || requests.length < 2) return [];
    const fn = isDong ? getMonthlyChangesFromDaily : getRecentChanges;
    return fn(responses.getResponse(requests[1]));
  }, [initialized, responses, requests, isDong]);

  const chartTitle = isDong ? "매물시세" : "매전지수";
  const yAxisName = isDong ? "만원" : "지수";
  const magnification = isDong ? 10000 : 1;

  const chartContent = seriesData.length > 0 ? (
    <>
      <Divider sx={{ my: 0.5 }} />
      {/* 지수 변동율 테이블 */}
      {(dealChanges.length > 0 || leaseChanges.length > 0) && (
        <Box sx={{ mb: 0.3 }}>
          <ChangeRateTable changes={dealChanges} label="매매" />
          <ChangeRateTable changes={leaseChanges} label="전세" />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, fontWeight: 600 }}>{chartTitle}</Typography>
      <CardChart
        seriesData={seriesData}
        yAxisName={yAxisName}
        magnification={magnification}
        height={180}
        xTickDays={isDong ? 365 : 365 * 3}
        yearRange={isDong ? null : 10}
      />
    </>
  ) : null;

  // showChart=true: 차트 그대로
  // showChart=false: "차트 보기/숨기기" 토글 버튼 + 펼쳐졌을 때만 차트
  let chart;
  if (showChart) {
    chart = chartContent;
  } else {
    chart = (
      <>
        <Divider sx={{ my: 0.3 }} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.3}
          onClick={(e) => { e.stopPropagation(); setChartExpanded((v) => !v); }}
          sx={{
            py: 0.5,
            cursor: "pointer",
            borderRadius: 1,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, color: "text.secondary" }}>
            {chartExpanded ? "차트 숨기기" : "차트 보기"}
          </Typography>
          {chartExpanded ? <MdExpandLess size={14} /> : <MdExpandMore size={14} />}
        </Stack>
        {chartExpanded && chartContent}
      </>
    );
  }

  return (
    <RegionSummaryCard
      region={region}
      onClick={onClick}
      onOpenNewWindow={openRegionNewWindow}
      chart={chart}
      fullWidth={fullWidth}
      showStar={showStar}
      hideTitle={hideTitle}
    />
  );
};
